import { z } from "zod";
import { NotePageSchema } from "../../shared/customer-retrieval-schemas";
import { CustomerPrimaryContactSchema } from "../../shared/customer-record-schemas";
import {
  CommercialDocumentHubLinkSchema,
  HubAssignmentsSchema,
  HubBillingSchema,
  HubDocumentsSchema,
  HubOverviewListSchema,
  HubRecordInputSchema,
  HubRecordResponseSchema,
  hubRecordFromContextItem,
  ToolHubInputSchema,
  ToolHubListSchema,
  ToolHubResponseSchema,
  type HubRecordInput,
  type ToolHubInput,
} from "../../shared/tool-hubs";
import { parseResponse } from "../api/client-core";
import { prepareArtifactUpload, uploadArtifact } from "./api";

const root = (organizationId: string) => `/api/ops/customers/${encodeURIComponent(organizationId)}`;
const json = (method: string, body: unknown): RequestInit => ({
  method,
  credentials: "same-origin",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

export async function listToolHubs(organizationId: string) {
  return parseResponse(
    await fetch(`${root(organizationId)}/hubs`, { credentials: "same-origin" }),
    ToolHubListSchema,
  );
}

export async function getToolHub(organizationId: string, hubId: string) {
  return parseResponse(
    await fetch(`${root(organizationId)}/hubs/${encodeURIComponent(hubId)}`, {
      credentials: "same-origin",
    }),
    ToolHubResponseSchema,
  );
}

export async function saveToolHub(
  organizationId: string,
  hubId: string | null,
  input: ToolHubInput,
) {
  const body = ToolHubInputSchema.parse(input);
  return parseResponse(
    await fetch(
      hubId
        ? `${root(organizationId)}/hubs/${encodeURIComponent(hubId)}`
        : `${root(organizationId)}/hubs`,
      json(hubId ? "PUT" : "POST", body),
    ),
    ToolHubResponseSchema,
  );
}

const HubDocumentsResponseSchema = z.strictObject({
  documents: HubDocumentsSchema,
  billing: HubBillingSchema,
});
export async function getToolHubDocuments(organizationId: string, hubId: string) {
  return parseResponse(
    await fetch(`${root(organizationId)}/hubs/${encodeURIComponent(hubId)}/documents`, {
      credentials: "same-origin",
    }),
    HubDocumentsResponseSchema,
  );
}

export async function linkDocumentToHub(
  organizationId: string,
  documentId: string,
  hubId: string | null,
) {
  return parseResponse(
    await fetch(
      `${root(organizationId)}/commercial-documents/${encodeURIComponent(documentId)}/hub`,
      json("PUT", { hubId }),
    ),
    CommercialDocumentHubLinkSchema,
  );
}

/** Up to 200 of the hub's records, read through the existing tool-scoped notes endpoint. */
export async function listToolHubRecords(organizationId: string, hubId: string) {
  const query = new URLSearchParams({ scope: "tool", toolId: hubId, limit: "200" });
  const page = await parseResponse(
    await fetch(`${root(organizationId)}/context/notes?${query}`, { credentials: "same-origin" }),
    NotePageSchema,
  );
  return { records: page.notes.map(hubRecordFromContextItem), hasMore: page.page.hasMore };
}

export async function createToolHubRecord(
  organizationId: string,
  hubId: string,
  input: HubRecordInput,
) {
  return parseResponse(
    await fetch(
      `${root(organizationId)}/hubs/${encodeURIComponent(hubId)}/records`,
      json("POST", HubRecordInputSchema.parse(input)),
    ),
    HubRecordResponseSchema,
  );
}

/** Uploads a transcript or other file into the hub's scope so search and agents can read it. */
export async function uploadToolHubFile(
  organizationId: string,
  hubId: string,
  file: File,
  title: string,
) {
  const reservation = await prepareArtifactUpload(organizationId, {
    sourceType: "file",
    title: title.slice(0, 240) || file.name.slice(0, 240),
    filename: file.name.slice(0, 255),
    mediaType: file.type || "text/plain",
    byteCount: file.size,
    sourceCreatedAt: null,
    sourceModifiedAt: file.lastModified ? Math.floor(file.lastModified / 1000) : null,
    sourceSystem: null,
    sourceRecordId: null,
    toolId: hubId,
  });
  const uploaded = await uploadArtifact(organizationId, reservation, file);
  return uploaded.artifact.id;
}

/** Every hub with its next step, contract documents, and newest records (ADR-0088). */
export async function getHubOverview(organizationId: string) {
  return parseResponse(
    await fetch(`${root(organizationId)}/hubs/overview`, { credentials: "same-origin" }),
    HubOverviewListSchema,
  );
}

export async function getHubAssignments(organizationId: string) {
  return parseResponse(
    await fetch(`${root(organizationId)}/hubs/assignments`, { credentials: "same-origin" }),
    HubAssignmentsSchema,
  );
}

export async function linkLibraryDocumentToHub(
  organizationId: string,
  documentId: string,
  hubId: string | null,
) {
  return parseResponse(
    await fetch(
      `${root(organizationId)}/customer-documents/${encodeURIComponent(documentId)}/hub`,
      json("PUT", { hubId }),
    ),
    CommercialDocumentHubLinkSchema,
  );
}

export async function getPrimaryContact(organizationId: string) {
  return parseResponse(
    await fetch(`${root(organizationId)}/primary-contact`, { credentials: "same-origin" }),
    CustomerPrimaryContactSchema,
  );
}

export async function setPrimaryContact(organizationId: string, personId: string) {
  return parseResponse(
    await fetch(`${root(organizationId)}/primary-contact`, json("PUT", { personId })),
    CustomerPrimaryContactSchema,
  );
}

export const customerPageKeys = {
  overview: (organizationId: string) => ["ops", "tool-hubs", organizationId, "overview"] as const,
  assignments: (organizationId: string) =>
    ["ops", "tool-hubs", organizationId, "assignments"] as const,
  primaryContact: (organizationId: string) =>
    ["ops", "customers", organizationId, "primary-contact"] as const,
  context: (organizationId: string) => ["ops", "context", organizationId] as const,
};
